import React, { useEffect, useMemo, useState } from 'react';
import { guardarClientesAsignados, obtenerClientesAsignados } from '../db/indexedDB';
import { API_BASE, formatApiError, getToken, isAuthError, notifySessionExpired } from '../services/api';

const estadoClienteLabel = (estado) => ({
  pendiente: 'Pendiente',
  asignado: 'Asignado',
  reprogramado: 'Reprogramado',
  revision_admin: 'Revision admin',
}[estado] || estado || 'Pendiente');

const clienteVisibleParaTecnico = (cliente) => !['finalizado', 'revision_admin', 'reprogramado'].includes(cliente.estado);

const estadoClientePrioridad = (estado) => {
  if (estado === 'reprogramado') return 0;
  if ((estado || 'pendiente') === 'pendiente') return 1;
  if (estado === 'asignado') return 2;
  return 3;
};

const montoCliente = (cliente) => Number(cliente?.deuda_vencida || 0);

const prioridadAdminCliente = (cliente) => Number(cliente?.prioridad_admin || 0);

const formatearMonto = (valor) => `$${Number(valor || 0).toLocaleString('es-CL')}`;

const DashboardTecnico = ({ onSelectCliente }) => {
  const [clientes, setClientes] = useState([]);
  const [filtroComuna, setFiltroComuna] = useState('');
  const [filtroCodigo, setFiltroCodigo] = useState('');
  const [filtroGestion, setFiltroGestion] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch(`${API_BASE}/tecnicos/mis-clientes`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(async (res) => {
        const data = await res.json().catch(() => null);
        if (!res.ok) {
          if (isAuthError(res, data)) notifySessionExpired();
          throw new Error(formatApiError(res, data, 'No se pudieron cargar las visitas asignadas.'));
        }
        const clientesAsignados = Array.isArray(data) ? data : [];
        setClientes(clientesAsignados);
        await guardarClientesAsignados(clientesAsignados);
      })
      .catch(async (err) => {
        const cachedClientes = await obtenerClientesAsignados().catch(() => []);
        const visibles = cachedClientes.filter(clienteVisibleParaTecnico);
        if (visibles.length) {
          setClientes(visibles);
          setError('Sin senal: usando visitas guardadas en este dispositivo.');
          return;
        }
        setError(err.message);
      })
      .finally(() => setLoading(false));
  }, []);

  const comunasDisponibles = useMemo(() => (
    [...new Set(clientes.map((cliente) => cliente.comuna).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b))
  ), [clientes]);

  const clientesPorComuna = useMemo(() => (
    clientes.filter((cliente) => !filtroComuna || cliente.comuna === filtroComuna)
  ), [clientes, filtroComuna]);

  const codigosDisponibles = useMemo(() => (
    [...new Map(clientesPorComuna.map((cliente) => [cliente.cod_cliente, cliente])).values()]
      .sort((a, b) => String(a.cod_cliente).localeCompare(String(b.cod_cliente)))
  ), [clientesPorComuna]);

  const gestionesDisponibles = useMemo(() => (
    clientesPorComuna
      .filter((cliente) => !filtroCodigo || cliente.cod_cliente === filtroCodigo)
      .sort((a, b) => String(a.num_gestion).localeCompare(String(b.num_gestion)))
  ), [clientesPorComuna, filtroCodigo]);

  useEffect(() => {
    if (filtroCodigo && !codigosDisponibles.some((cliente) => cliente.cod_cliente === filtroCodigo)) {
      setFiltroCodigo('');
      setFiltroGestion('');
    }
  }, [codigosDisponibles, filtroCodigo]);

  useEffect(() => {
    if (filtroGestion && !gestionesDisponibles.some((cliente) => cliente.num_gestion === filtroGestion)) {
      setFiltroGestion('');
    }
  }, [gestionesDisponibles, filtroGestion]);

  const resumenTecnico = useMemo(() => ({
    total: clientes.length,
    reprogramados: clientes.filter((cliente) => cliente.estado === 'reprogramado').length,
    pendientes: clientes.filter((cliente) => (cliente.estado || 'pendiente') === 'pendiente').length,
    comunas: comunasDisponibles.length,
    montoMayor: Math.max(0, ...clientes.map(montoCliente)),
    prioridadAdmin: clientes.filter((cliente) => prioridadAdminCliente(cliente) > 0).length,
  }), [clientes, comunasDisponibles.length]);

  const clientesFiltrados = useMemo(() => (
    clientes.filter((cliente) => (
      (!filtroComuna || cliente.comuna === filtroComuna) &&
      (!filtroCodigo || cliente.cod_cliente === filtroCodigo) &&
      (!filtroGestion || cliente.num_gestion === filtroGestion) &&
      (!filtroEstado || (cliente.estado || 'pendiente') === filtroEstado)
    )).sort((a, b) => (
      prioridadAdminCliente(b) - prioridadAdminCliente(a) ||
      montoCliente(b) - montoCliente(a) ||
      estadoClientePrioridad(a.estado) - estadoClientePrioridad(b.estado) ||
      String(a.comuna || '').localeCompare(String(b.comuna || '')) ||
      String(a.cod_cliente || '').localeCompare(String(b.cod_cliente || ''))
    ))
  ), [clientes, filtroComuna, filtroCodigo, filtroGestion, filtroEstado]);

  const filtrosActivos = Boolean(filtroComuna || filtroCodigo || filtroGestion || filtroEstado);

  const limpiarFiltros = () => {
    setFiltroComuna('');
    setFiltroCodigo('');
    setFiltroGestion('');
    setFiltroEstado('');
  };

  return (
    <div className="technician-dashboard">
      <div className="technician-hero">
        <div>
          <p className="gv-section-title">Operacion en terreno</p>
          <h2>Mis Visitas Asignadas</h2>
          <p>Casos activos ordenados para revisar primero reprogramados y pendientes.</p>
        </div>
      </div>

      {error && <div className="gv-alert gv-alert--error">{error}</div>}
      {loading && <p className="import-empty">Cargando visitas asignadas...</p>}

      {!loading && !error && (
        <div className="technician-summary">
          <button
            type="button"
            className={`technician-summary__item${!filtroEstado ? ' active' : ''}`}
            onClick={() => setFiltroEstado('')}
          >
            <span>Total asignadas</span>
            <strong>{resumenTecnico.total}</strong>
          </button>
          <button
            type="button"
            className={`technician-summary__item warn${filtroEstado === 'reprogramado' ? ' active' : ''}`}
            onClick={() => setFiltroEstado('reprogramado')}
          >
            <span>Reprogramadas</span>
            <strong>{resumenTecnico.reprogramados}</strong>
          </button>
          <button
            type="button"
            className={`technician-summary__item${filtroEstado === 'pendiente' ? ' active' : ''}`}
            onClick={() => setFiltroEstado('pendiente')}
          >
            <span>Pendientes</span>
            <strong>{resumenTecnico.pendientes}</strong>
          </button>
          <div className="technician-summary__item">
            <span>Comunas</span>
            <strong>{resumenTecnico.comunas}</strong>
          </div>
          <div className="technician-summary__item priority">
            <span>Monto mayor</span>
            <strong>{formatearMonto(resumenTecnico.montoMayor)}</strong>
          </div>
          <div className="technician-summary__item admin-priority">
            <span>Prioridad admin</span>
            <strong>{resumenTecnico.prioridadAdmin}</strong>
          </div>
        </div>
      )}

      <div className="technician-filters">
        <div>
          <label className="gv-label">Comuna</label>
          <select
            value={filtroComuna}
            onChange={(e) => {
              setFiltroComuna(e.target.value);
              setFiltroCodigo('');
              setFiltroGestion('');
            }}
            className="gv-select"
          >
            <option value="">Todas las comunas</option>
            {comunasDisponibles.map((comuna) => (
              <option key={comuna} value={comuna}>{comuna}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="gv-label">Cliente</label>
          <select
            value={filtroCodigo}
            onChange={(e) => {
              setFiltroCodigo(e.target.value);
              setFiltroGestion('');
            }}
            className="gv-select"
          >
            <option value="">Todos los clientes</option>
            {codigosDisponibles.map((cliente) => (
              <option key={cliente.cod_cliente} value={cliente.cod_cliente}>
                {cliente.cod_cliente} - {cliente.r_social}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="gv-label">Gestion</label>
          <select
            value={filtroGestion}
            onChange={(e) => setFiltroGestion(e.target.value)}
            className="gv-select"
          >
            <option value="">Todas las gestiones</option>
            {gestionesDisponibles.map((cliente) => (
              <option key={`${cliente.cod_cliente}-${cliente.num_gestion}`} value={cliente.num_gestion}>
                {cliente.num_gestion} | {cliente.cod_cliente}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="gv-label">Estado</label>
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="gv-select"
          >
            <option value="">Todos los estados</option>
            <option value="reprogramado">Reprogramados</option>
            <option value="pendiente">Pendientes</option>
            <option value="asignado">Asignados</option>
          </select>
        </div>

        {filtrosActivos && (
          <button type="button" className="gv-btn gv-btn--outline technician-filters__clear" onClick={limpiarFiltros}>
            Limpiar filtros
          </button>
        )}
      </div>

      <div className="technician-list-context">
        <div>
          <span>Mostrando</span>
          <strong>{clientesFiltrados.length} de {clientes.length} caso(s)</strong>
        </div>
        <small>Orden: prioridad admin, luego mayor monto vencido</small>
      </div>

      <div className="technician-client-list">
        {!loading && clientesFiltrados.map((cliente, index) => (
          <article
            key={cliente.id}
            className={`technician-client-card technician-client-card--${cliente.estado || 'pendiente'}${index === 0 && montoCliente(cliente) > 0 ? ' technician-client-card--top-amount' : ''}${prioridadAdminCliente(cliente) > 0 ? ' technician-client-card--admin-priority' : ''}`}
          >
            <div className="technician-client-card__main">
              <div className="technician-client-card__top">
                <span className="technician-client-card__code">{cliente.cod_cliente}</span>
                <span className={`technician-client-card__state technician-client-card__state--${cliente.estado || 'pendiente'}`}>
                  {estadoClienteLabel(cliente.estado)}
                </span>
              </div>
              <h3>{cliente.r_social}</h3>
              <p className="technician-client-card__address">
                {cliente.direccion}{cliente.comuna ? `, ${cliente.comuna}` : ''}
              </p>
              <div className="technician-client-card__meta">
                <div>
                  <span>Gestion</span>
                  <strong>{cliente.num_gestion || '-'}</strong>
                </div>
                <div>
                  <span>Comuna</span>
                  <strong>{cliente.comuna || 'Sin comuna'}</strong>
                </div>
                {cliente.deuda_vencida !== undefined && (
                  <div>
                    <span>Deuda</span>
                    <strong>{formatearMonto(cliente.deuda_vencida)}</strong>
                  </div>
                )}
              </div>
              <div className="technician-client-card__chips">
                {prioridadAdminCliente(cliente) > 0 && <em className="admin-priority">Prioridad admin</em>}
                {index === 0 && montoCliente(cliente) > 0 && <em className="priority">Prioridad monto</em>}
                {cliente.estado === 'reprogramado' && <em className="warn">Nueva visita</em>}
              </div>
            </div>
            <div className="technician-client-card__actions">
              <button
                type="button"
                className="gv-btn gv-btn--primary"
                onClick={() => onSelectCliente?.(cliente)}
              >
                Atender visita
              </button>
              <span>Ficha y registro</span>
            </div>
          </article>
        ))}

        {!loading && clientesFiltrados.length === 0 && (
          <p className="import-empty">No hay visitas reales asignadas con esos filtros.</p>
        )}
      </div>
    </div>
  );
};

export default DashboardTecnico;
